// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/version/self-update.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/version/src/self-update.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo vigia-fleet-app
//     node fleet/fleet.ts check --repo vigia-fleet-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * Silent self-update for a client that fell below the server's supported API
 * contract window, WITH the anti-loop guard that makes it safe to ship.
 *
 * The happy path is invisible: the client notices it is too old, asks the
 * Service Worker to fetch the new build, and the existing PWA chain
 * (`updatefound` -> `SKIP_WAITING` -> `controllerchange` -> reload, see
 * `infra/PWA_UPDATES.md`) swaps it in. The user sees a reload and nothing else.
 *
 * The failure mode this guard exists for is the classic one: the client
 * reloads, the CDN edge is still serving the OLD bundle, the fresh page reaches
 * the same verdict and reloads again — forever. That is strictly worse than the
 * original problem, because the app never finishes booting. So every attempt is
 * PERSISTED (it has to survive the reload it causes — an in-memory counter is
 * wiped by the very thing it is counting) and capped. Past the cap we stop
 * reloading and hand the user something actionable instead.
 *
 * Storage failure is treated as "cannot count", and "cannot count" means "do not
 * reload". A guard that cannot remember its attempts cannot bound them, and an
 * unbounded reload loop is the one outcome worse than a visible banner.
 */

/** The slice of `Storage` this module needs. Injected so it is testable. */
export interface UpdateAttemptStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface AttemptRecord {
  /** Contract version of the build that started reloading. */
  from: number;
  /** `min_supported` it was chasing. */
  to: number;
  attempts: number;
  lastAt: number;
}

export type SelfUpdateOutcome =
  /** An update was requested; a reload is expected to follow. */
  | { action: "updating"; attempt: number }
  /** An update is already in flight in this page load; nothing more to do. */
  | { action: "in-flight" }
  /** Out of attempts (or unable to count them): degrade to a visible notice. */
  | { action: "give-up"; attempts: number };

export interface SelfUpdateControllerOptions {
  /** This build's API contract version. */
  clientVersion: number;
  /**
   * Persistent, origin-scoped storage. `localStorage`, or `null` where there is
   * none — in which case the controller never reloads (see module docs).
   */
  storage: UpdateAttemptStorage | null;
  /** Asks the platform to fetch and activate the new build. */
  triggerUpdate: () => void;
  /** Called when we stop trying. The UI turns this into an actionable notice. */
  onGiveUp: (info: { clientVersion: number; minSupported: number; attempts: number }) => void;
  /** Reload budget per (from -> to) pair. Defaults to 3. */
  maxAttempts?: number;
  /**
   * After this long without a new attempt, the budget resets. Without it a user
   * who burned the budget during a bad rollout would stay stuck on the notice
   * forever, even once the CDN caught up days later. Defaults to 1 hour.
   */
  resetAfterMs?: number;
  now?: () => number;
  storageKey?: string;
}

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_RESET_AFTER_MS = 60 * 60 * 1000;
const DEFAULT_STORAGE_KEY = "api-contract:self-update";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseRecord(raw: string | null): AttemptRecord | null {
  if (raw === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return null;
    }
    const { from, to, attempts, lastAt } = parsed;
    if (
      typeof from !== "number" ||
      typeof to !== "number" ||
      typeof attempts !== "number" ||
      typeof lastAt !== "number"
    ) {
      return null;
    }
    return { from, to, attempts, lastAt };
  } catch {
    return null;
  }
}

/**
 * Creates the controller. One per app instance; it holds a page-load-scoped
 * "already triggered" flag so the 15s health poll cannot fire N updates while
 * the first one is still installing.
 */
export function createSelfUpdateController(options: SelfUpdateControllerOptions) {
  const {
    clientVersion,
    storage,
    triggerUpdate,
    onGiveUp,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    resetAfterMs = DEFAULT_RESET_AFTER_MS,
    now = () => Date.now(),
    storageKey = DEFAULT_STORAGE_KEY,
  } = options;

  let updateInFlight = false;
  let gaveUp = false;

  function persist(record: AttemptRecord): boolean {
    if (!storage) {
      return false;
    }
    try {
      storage.setItem(storageKey, JSON.stringify(record));
      const readBack = parseRecord(storage.getItem(storageKey));
      return readBack !== null && readBack.attempts === record.attempts;
    } catch {
      return false;
    }
  }

  function read(): AttemptRecord | null {
    if (!storage) {
      return null;
    }
    try {
      return parseRecord(storage.getItem(storageKey));
    } catch {
      return null;
    }
  }

  return {
    handleOutdated(minSupported: number): SelfUpdateOutcome {
      if (updateInFlight || gaveUp) {
        return updateInFlight
          ? { action: "in-flight" }
          : { action: "give-up", attempts: maxAttempts };
      }

      const previous = read();
      const timestamp = now();
      const continues =
        previous?.from === clientVersion &&
        previous.to === minSupported &&
        timestamp - previous.lastAt < resetAfterMs;
      const attempts = (continues ? previous.attempts : 0) + 1;

      if (attempts > maxAttempts) {
        gaveUp = true;
        onGiveUp({ clientVersion, minSupported, attempts: attempts - 1 });
        return { action: "give-up", attempts: attempts - 1 };
      }

      const stored = persist({
        from: clientVersion,
        to: minSupported,
        attempts,
        lastAt: timestamp,
      });
      if (!stored) {
        gaveUp = true;
        onGiveUp({ clientVersion, minSupported, attempts: attempts - 1 });
        return { action: "give-up", attempts: attempts - 1 };
      }

      updateInFlight = true;
      triggerUpdate();
      return { action: "updating", attempt: attempts };
    },

    markCompatible(): void {
      if (!storage) {
        return;
      }
      try {
        if (storage.getItem(storageKey) !== null) {
          storage.removeItem(storageKey);
        }
      } catch {
        // Best-effort
      }
    },
  };
}
